<template>
  <div ref="containerRef" class="dot-slider">
    <span
        v-for="(_, index) in length"
        :key="index"
        :ref="el => setDotRef(el, index)"
        class="dot"
        :class="{ 'active-dot': index === 0 }"
        :style="getDotStyle(index)"
    />
  </div>
</template>

<script setup>
import {ref, computed, onMounted, watch, nextTick} from 'vue';

const props = defineProps({
  modelValue: {
    type: Number,
    default: 0
  },
  length: {
    type: Number,
    required: true,
    validator: (val) => val > 0
  },
  dotStyle: {
    type: Object,
    default: () => ({
      width: '20px',
      height: '20px',
      backgroundColor: '#c0d62a'
    })
  },
  activeDotStyle: {
    type: Object,
    default: () => ({
      backgroundColor: '#ac5c5c'
    })
  },
  duration: {
    type: String,
    default: '0.5s'
  }
});

const emit = defineEmits(['update:modelValue']);

const containerRef = ref(null);
const dotRefs = ref([]);
const spacing = ref(0);
const currentIndex = ref(props.modelValue);

// CSS переменная для активной точки
const activeOffset = computed(() => currentIndex.value * spacing.value);

// Сохраняем refs точек
const setDotRef = (el, index) => {
  if (el) {
    dotRefs.value[index] = el;
  }
};

// Вычисляем расстояние между точками
const calculateSpacing = async () => {
  await nextTick();

  if (dotRefs.value.length < 2) {
    spacing.value = 0;
    return;
  }

  const first = dotRefs.value[0]?.getBoundingClientRect();
  const second = dotRefs.value[1]?.getBoundingClientRect();

  if (first && second) {
    spacing.value = second.left - first.left;
  }
};

// Получаем стили для точки
const getDotStyle = (index) => {
  const isActive = index === 0; // Только первая точка — active-dot

  return {
    ...props.dotStyle,
    ...(isActive ? props.activeDotStyle : {}),
    ...(isActive ? {'--value': activeOffset.value} : {}),
    transition: `transform ${props.duration}, background-color ${props.duration}`
  };
};

// Публичные методы (как в оригинале)
const next = () => {
  if (currentIndex.value < props.length - 1) {
    currentIndex.value++;

    nextTick(() => {
      const dot = dotRefs.value[currentIndex.value];
      if (dot) {
        dot.style.transform = `translateX(-${spacing.value}px)`;
      }
    });
  } else {
    reset();
  }

  emit('update:modelValue', currentIndex.value);
};

const prev = () => {
  if (currentIndex.value > 0) {
    // ❗ Возвращаем ТЕКУЩУЮ точку на место
    const dot = dotRefs.value[currentIndex.value];
    if (dot) {
      dot.style.transform = 'translateX(0)';
    }

    currentIndex.value--;
  } else {
    currentIndex.value = props.length - 1;
    moveAllDotsLeft();
  }

  emit('update:modelValue', currentIndex.value);
};

const setIndex = (newIndex) => {
  if (newIndex < 0 || newIndex >= props.length) return;

  const oldIndex = currentIndex.value;

  // Если индекс не изменился
  if (oldIndex === newIndex) return;

  // Определяем направление
  const isForward = newIndex > oldIndex;

  if (isForward) {
    // Движение вперёд: сдвигаем все точки от старого до нового индекса
    for (let i = oldIndex + 1; i <= newIndex; i++) {
      nextTick(() => {
        const dot = dotRefs.value[i];
        if (dot) {
          dot.style.transform = `translateX(-${spacing.value}px)`;
        }
      });
    }
  } else {
    // Движение назад: возвращаем все точки от нового до старого индекса
    for (let i = newIndex + 1; i <= oldIndex; i++) {
      const dot = dotRefs.value[i];
      if (dot) {
        dot.style.transform = 'translateX(0)';
      }
    }
  }

  currentIndex.value = newIndex;
  emit('update:modelValue', newIndex);
};

const reset = () => {
  currentIndex.value = 0;

  // Возвращаем все точки на место
  dotRefs.value.forEach(dot => {
    if (dot && !dot.classList.contains('active-dot')) {
      dot.style.transform = 'translateX(0)';
    }
  });

  emit('update:modelValue', 0);
};

const moveAllDotsLeft = () => {
  dotRefs.value.forEach(dot => {
    if (dot && !dot.classList.contains('active-dot')) {
      dot.style.transform = `translateX(-${spacing.value}px)`;
    }
  });
};

// Синхронизация с v-model
watch(() => props.modelValue, (newVal) => {
  if (newVal !== currentIndex.value) {
    currentIndex.value = newVal;
  }
});

watch(() => props.length, () => {
  calculateSpacing();
});

onMounted(() => {
  calculateSpacing();
});

defineExpose({
  next,
  prev,
  setIndex,
  reset,
  currentIndex: computed(() => currentIndex.value)
});
</script>

<style scoped>
.dot-slider {
  display: inline-flex;
  gap: 0;
  position: relative;
}

.dot {
  display: inline-block;
  border-radius: 50%;
}

.dot:not(:last-child) {
  margin-right: 8px;
}

.active-dot {
  position: relative;
  opacity: 1 !important;
  margin-top: -2px;
  background-color: #ffffff;
  transform: translateX(calc(var(--value) * 1px));
}

.active-dot::after {
  content: '';
  width: 12px;
  height: 12px;
  border-radius: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  z-index: 1;
}
</style>
